/* eslint-disable max-lines */
import React, { useEffect } from 'react';
import { getSelectedCommunicationWithEvents } from 'selectors/communications';
import { useSelector } from 'store';

import { selectCampaignEnvelopeReview, setMatchNavigation } from 'actions';
import { NEW_CHEVRON_LEFT } from 'constants/filterIcons';
import { useDispatch } from 'react-redux';
import { getEnvelopeToPreview } from 'selectors/envelopeListView';
import {
  getCampaignsRulesFromCommunication,
  getMatchNavigation,
  getSelectedCampaign,
  getSelectedRule,
  getSelectedSection,
} from 'selectors/envelopeReview';
import { getRuleLines } from 'selectors/events';

const EnvelopePreviewMatchNavigation: React.FC = () => {
  const dispatch = useDispatch();

  const selectedCommunication = useSelector(getSelectedCommunicationWithEvents);
  const selectedRule = useSelector(getSelectedRule);
  const selectedCampaign = useSelector(getSelectedCampaign);
  const selectedSection = useSelector(getSelectedSection);
  const { index: navigationIndex } = useSelector(getMatchNavigation);
  const envelopePreview = useSelector(getEnvelopeToPreview);
  const campaignsRules = useSelector((state) =>
    getCampaignsRulesFromCommunication(state, selectedCommunication?.uuid || '')
  );

  const ruleLines = useSelector((state) =>
    getRuleLines(
      state,
      selectedCommunication?.uuid || '',
      (selectedSection === 'history').toString(),
      (selectedSection === 'history').toString()
    )
  );

  let totalNavigationItems = 0;

  const navigationItems = Object.entries(ruleLines)
    .reduce<
      {
        campaignId?: string;
        line: number;
        referenceNodes: number[];
      }[]
    >((acc, [rKey, curValue]) => {
      if (campaignsRules) {
        const campaign = Object.entries(campaignsRules).find(([, campaignWithRules]) => {
          if (campaignWithRules.rules.find((r) => r.rule_group_uuid === rKey)) {
            return true;
          }
          return false;
        });

        if (campaign) {
          const ruleLineWithCampaign = curValue.map((value) => ({
            ...value,
            campaignId: campaign[0],
          }));
          return [...acc, ...ruleLineWithCampaign];
        }
      }

      return [...acc, ...curValue];
    }, [])
    .sort((a, b) => {
      if (a.campaignId && b.campaignId && a.campaignId > b.campaignId) return -1;
      if (a.campaignId && b.campaignId && a.campaignId < b.campaignId) return 1;
      if (a.line > b.line) return 1;
      if (a.line < b.line) return -1;
      if (a.referenceNodes[0] > b.referenceNodes[0]) return 1;
      return -1;
    });

  totalNavigationItems = Object.entries(ruleLines).reduce(
    (acc, [, curValue]) => acc + curValue.length,
    0
  );

  // cleanup
  useEffect(
    () => () => {
      dispatch(
        setMatchNavigation({
          index: 0,
          line: 0,
          referenceNodes: [],
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // selecting different campaign/rule
  useEffect(() => {
    dispatch(
      setMatchNavigation({
        index: 0,
        line: navigationItems[0]?.line || 0,
        referenceNodes: navigationItems[0]?.referenceNodes || [],
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [envelopePreview?.envelopeId, dispatch]);

  // scroll control
  useEffect(() => {
    if (navigationIndex === 0) {
      const element = document.getElementById('envelope-content');
      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }

    if (selectedCampaign) {
      const element = document
        .querySelector(
          `div[data-campaign-id="${selectedCampaign}"][data-index="${
            navigationItems[navigationIndex - 1]?.line
          }"]`
        )
        ?.querySelector(`#supernode-${navigationItems[navigationIndex - 1].referenceNodes[0]}`);

      if (element && element.scrollIntoView) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRule, selectedCampaign, navigationIndex]);

  const handlePreviousMatch = (): void => {
    if (navigationIndex > 1) {
      const item = navigationItems[navigationIndex - 2];
      dispatch(selectCampaignEnvelopeReview(item.campaignId || ''));

      dispatch(
        setMatchNavigation({
          index: navigationIndex - 1,
          line: navigationItems[navigationIndex - 2].line,
          referenceNodes: navigationItems[navigationIndex - 2].referenceNodes,
        })
      );
    } else {
      const item = navigationItems[navigationIndex - 1];
      dispatch(selectCampaignEnvelopeReview(item.campaignId || ''));

      dispatch(
        setMatchNavigation({
          index: navigationIndex - 1,
          line: navigationItems[navigationIndex - 1].line,
          referenceNodes: navigationItems[navigationIndex - 1].referenceNodes,
        })
      );
    }
  };

  const handleNextMatch = (): void => {
    const item = navigationItems[navigationIndex];
    dispatch(selectCampaignEnvelopeReview(item.campaignId || ''));

    dispatch(
      setMatchNavigation({
        index: navigationIndex + 1,
        line: navigationItems[navigationIndex].line,
        referenceNodes: navigationItems[navigationIndex].referenceNodes,
      })
    );
  };

  let currentIndex = navigationIndex;
  currentIndex = currentIndex <= totalNavigationItems ? currentIndex : totalNavigationItems;

  if (totalNavigationItems < 1) return null;

  return (
    <div className="flex flex-row gap-2 items-center self-end">
      <span className="whitespace-no-wrap">
        {currentIndex} of {totalNavigationItems} matches
      </span>
      <button
        type="button"
        className={`flex justify-center w-4 focus:outline-none ${
          (selectedRule && currentIndex === 1) || currentIndex === 0
            ? 'opacity-50 cursor-default'
            : ''
        }`}
        onClick={handlePreviousMatch}
        disabled={currentIndex === 0}
      >
        {NEW_CHEVRON_LEFT}
      </button>
      <button
        type="button"
        className={`flex justify-center w-4 transform rotate-180 focus:outline-none ${
          currentIndex === totalNavigationItems ? 'opacity-50 cursor-default' : ''
        }`}
        onClick={handleNextMatch}
        disabled={currentIndex === totalNavigationItems}
      >
        {NEW_CHEVRON_LEFT}
      </button>
    </div>
  );
};
export default EnvelopePreviewMatchNavigation;
